import { LogIn, LogOut, ShieldCheck, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '@/shared/i18n/useT';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';
import { buttonVariants } from '../ui/buttonVariants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';

export type AccountInfo =
  | { status: 'anon' }
  | {
      status: 'auth';
      nombre: string | null;
      email: string;
      isAdmin: boolean;
      onSignOut: () => void;
    };

const initialOf = (nombre: string | null, email: string): string =>
  (nombre?.trim() || email).charAt(0).toUpperCase();

/** Entrar (sin sesión) o menú de la cuenta (con sesión): Mi cuenta, Administración (solo admin) y Salir. */
export function AccountMenu({ account }: { account: AccountInfo }) {
  const { t } = useT();

  if (account.status === 'anon') {
    return (
      <Link
        to="/cuenta/entrar"
        aria-label={t('actions.signIn')}
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'md' }),
          'size-11 gap-2 px-0 sm:w-auto sm:px-3',
        )}
      >
        <Icon icon={LogIn} />
        <span className="hidden sm:inline">{t('actions.signIn')}</span>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('aria.accountMenu')}
          className="grid size-11 place-items-center rounded-full"
        >
          <span
            aria-hidden="true"
            className="grid size-8 place-items-center rounded-full bg-primary text-sm font-bold text-primary-fg"
          >
            {initialOf(account.nombre, account.email)}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block truncate text-sm font-semibold text-fg">
            {account.nombre || account.email}
          </span>
          {account.nombre ? (
            <span className="block truncate text-xs font-normal text-fg-muted">
              {account.email}
            </span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/cuenta">
            <Icon icon={UserRound} size={16} className="text-fg-muted" />
            {t('nav.cuenta')}
          </Link>
        </DropdownMenuItem>
        {account.isAdmin ? (
          <DropdownMenuItem asChild>
            <Link to="/admin">
              <Icon icon={ShieldCheck} size={16} className="text-fg-muted" />
              {t('nav.admin')}
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={account.onSignOut}>
          <Icon icon={LogOut} size={16} className="text-fg-muted" />
          {t('actions.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
